import { isPlatformBrowser } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Accessibility } from '@freelancer/accessibility';
import { Datastore } from '@freelancer/datastore';
import type { SiteStatsCollection } from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { Localization } from '@freelancer/localization';
import { TimeUtils } from '@freelancer/time-utils';
import { ContainerSize } from '@freelancer/ui/container';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor } from '@freelancer/ui/link';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import { PictureDisplay, PictureLoadMethod } from '@freelancer/ui/picture';
import { SocialButtonColor } from '@freelancer/ui/social-buttons';
import type { Observable } from 'rxjs';
import { combineLatest, NEVER } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-footer-freelancer',
  template: `
    <div
      class="FooterSection"
      flTrackingSection="Footer"
    >
      <fl-container
        [size]="size"
        [mobilePadding]="true"
      >
        <fl-grid>
          <fl-col [colDesktopSmall]="2">
            <fl-link
              [link]="'/'"
              [flTrackingLabel]="'flLogo'"
              class="FooterInfo mb-small tablet:mb-large"
            >
              <fl-logo
                [size]="LogoSize.SMALL"
                [backgroundColor]="BackgroundColor.DARK"
              >
              </fl-logo>
            </fl-link>

            <fl-language-switcher
              class="FooterInfo mb-small"
            ></fl-language-switcher>

            <div class="FooterInfo mb-small">
              <fl-icon
                class="FooterInfoIcon mr-xsmall"
                [name]="'ui-help'"
                [color]="IconColor.LIGHT"
                [useIconFont]="true"
                [fillIconFont]="true"
              >
              </fl-icon>
              <fl-link
                i18n="Footer link label"
                [link]="'/support'"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToSupport'"
              >
                Help & Support
              </fl-link>
            </div>
            <div class="FooterInfo mb-small">
              <fl-icon
                class="FooterInfoIcon mr-xsmall"
                [name]="'ui-accessibility'"
                [useIconFont]="true"
                [size]="IconSize.MID"
                [color]="IconColor.LIGHT"
              >
              </fl-icon>
              <fl-link
                id="triggerAccessibility"
                i18n="Footer link label"
                (click)="handleAccessibility()"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'openUserWayWidget'"
              >
                Accessibility
              </fl-link>
            </div>
          </fl-col>

          <fl-col
            [colTablet]="3"
            [colDesktopSmall]="2"
          >
            <p
              class="text-light text-body-16 font-bold mb-xsmall"
              i18n="Footer column title"
            >
              Freelancer
            </p>

            <div class="FooterNav mb-small">
              <fl-link
                *flFeature="Feature.DEPRECATED_SEARCH_PAGES"
                class="FooterNav-item"
                link="/job"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToJob'"
              >
                Categories
              </fl-link>

              <fl-link
                *flFeature="
                  Feature.DEPRECATED_SEARCH_PAGES;
                  else newSearchProjectsLink
                "
                class="FooterNav-item"
                link="/jobs"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToJobs'"
              >
                Projects
              </fl-link>

              <ng-template #newSearchProjectsLink>
                <fl-link
                  *flFeature="Feature.SEARCH"
                  class="FooterNav-item"
                  i18n="Footer link label"
                  flTrackingLabel="goToJobs"
                  [color]="LinkColor.LIGHT"
                  [link]="'/search/projects'"
                >
                  Projects
                </fl-link>
              </ng-template>

              <fl-link
                *flFeature="Feature.CONTESTS"
                class="FooterNav-item"
                link="/contest"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToContest'"
              >
                Contests
              </fl-link>

              <fl-link
                *flFeature="
                  Feature.DEPRECATED_SEARCH_PAGES;
                  else newSearchFreelancersLink
                "
                class="FooterNav-item"
                link="/freelancers"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToFreelancers'"
              >
                Freelancers
              </fl-link>

              <ng-template #newSearchFreelancersLink>
                <fl-link
                  *flFeature="Feature.SEARCH"
                  class="FooterNav-item"
                  flTrackingLabel="goToJobs"
                  i18n="Footer link label"
                  [color]="LinkColor.LIGHT"
                  [link]="'/search/users'"
                >
                  Freelancers
                </fl-link>
              </ng-template>

              <fl-link
                *flFeature="Feature.ENTERPRISE_PAGE"
                class="FooterNav-item"
                link="/enterprise/"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToEnterprise'"
              >
                Enterprise
              </fl-link>

              <fl-link
                *flFeature="Feature.INNOVATION_CHALLENGES"
                class="FooterNav-item"
                link="/innovation"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToInnovationChallenges'"
              >
                Innovation Challenges
              </fl-link>

              <fl-link
                *flFeature="Feature.AI_PAGE"
                class="FooterNav-item"
                link="/ai"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToAIPage'"
              >
                AI Development
              </fl-link>

              <fl-link
                *flFeature="Feature.MEMBERSHIPS"
                class="FooterNav-item"
                link="/membership"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToMembership'"
              >
                Membership
              </fl-link>

              <fl-link
                *flFeature="Feature.PREFERRED_FREELANCER_PROGRAM"
                class="FooterNav-item"
                link="/preferred-freelancer-program"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToPreferredFreelancerProgram'"
              >
                Preferred Freelancer Program
              </fl-link>

              <fl-link
                *flFeature="Feature.PROJECT_MANAGEMENT_PAGE"
                class="FooterNav-item"
                link="/project-management"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToProjectManager'"
              >
                Project Management
              </fl-link>

              <fl-link
                *flFeature="Feature.LOCAL_JOBS"
                class="FooterNav-item"
                link="/local"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToLocal'"
              >
                Local Jobs
              </fl-link>

              <fl-link
                link="https://www.photoanywhere.com"
                class="FooterNav-item"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToPhotoAnywhere'"
              >
                Photo Anywhere
              </fl-link>

              <fl-link
                *flFeature="Feature.SHOWCASE"
                class="FooterNav-item"
                link="/showcase"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToShowcase'"
              >
                Showcase
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="https://developers.freelancer.com"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToApi'"
              >
                API for Developers
              </fl-link>

              <fl-link
                *flFeature="Feature.FREELANCER_VERIFIED"
                class="FooterNav-item"
                link="/verified"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToVerified'"
              >
                Get Verified
              </fl-link>
            </div>
          </fl-col>

          <fl-col [colTablet]="2">
            <p
              class="text-light text-body-16 font-bold mb-xsmall"
              i18n="Footer column title"
            >
              About
            </p>
            <div class="FooterNav mb-small">
              <fl-link
                class="FooterNav-item"
                link="/about"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToAbout'"
              >
                About us
              </fl-link>

              <fl-link
                *flFeature="Feature.HOW_IT_WORKS_PAGE"
                class="FooterNav-item"
                link="/info/how-it-works/"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToHowItWorks'"
              >
                How it Works
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/about/security"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToSecurity'"
              >
                Security
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/investor"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToInvestor'"
              >
                Investor
              </fl-link>

              <fl-link
                *flFeature="Feature.SITE_MAP"
                class="FooterNav-item"
                link="/sitemap"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToSitemap'"
              >
                Sitemap
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/about/stories"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'GoToStories'"
              >
                Stories
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/about/media"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToInTheNews'"
              >
                News
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/about/management"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToManagement'"
              >
                Team
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/about/awards"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToAwards'"
              >
                Awards
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/about/press"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToPress'"
              >
                Press Releases
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/careers"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToCareers'"
              >
                Careers
              </fl-link>
            </div>
          </fl-col>
          <fl-col [colTablet]="2">
            <p
              class="text-light text-body-16 font-bold mb-xsmall"
              i18n="Footer column title"
            >
              Terms
            </p>

            <div class="FooterNav mb-small">
              <fl-link
                class="FooterNav-item"
                link="/about/privacy"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToPrivacy'"
              >
                Privacy Policy
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/about/terms"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToTerms'"
              >
                Terms and Conditions
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/dmca"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToCopyright'"
              >
                Copyright Policy
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="/info/codeofconduct"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToCodeOfConduct'"
              >
                Code of Conduct
              </fl-link>

              <fl-link
                *flFeature="Feature.BILLING"
                class="FooterNav-item"
                link="/feesandcharges"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToFeesAndCharges'"
              >
                Fees and Charges
              </fl-link>
            </div>
          </fl-col>

          <fl-col [colTablet]="2">
            <p
              class="text-light text-body-16 font-bold mb-xsmall"
              i18n="Footer column title"
            >
              Partners
            </p>

            <div class="FooterNav mb-small">
              <fl-link
                class="FooterNav-item"
                link="https://www.escrow.com"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToEscrow'"
              >
                Escrow.com
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="https://www.loadshift.com.au"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToLoadshift'"
                [rel]="'nofollow'"
              >
                Loadshift
              </fl-link>

              <fl-link
                class="FooterNav-item"
                link="https://www.warriorforum.com"
                i18n="Footer link label"
                [color]="LinkColor.LIGHT"
                [flTrackingLabel]="'goToWarrior'"
              >
                Warrior Forum
              </fl-link>
            </div>
          </fl-col>

          <fl-col
            *flFeature="Feature.FREELANCER_APPS"
            [colTablet]="3"
            [colDesktopSmall]="2"
          >
            <p
              class="text-light text-body-16 font-bold mb-xsmall"
              i18n="Footer column title"
            >
              Apps
            </p>

            <div class="FooterAppStoreIcons">
              <fl-link
                class="FooterMobileLink mb-mid mr-small tablet:mr-none"
                [link]="'/download'"
                [flTrackingLabel]="'goToIOsApp'"
              >
                <fl-picture
                  alt="Apple App Store logo"
                  i18n-alt="Footer App Install logo alt text"
                  [display]="PictureDisplay.BLOCK"
                  [height]="40"
                  [loadMethod]="PictureLoadMethod.LAZY"
                  [src]="'footer/app-store.svg'"
                  [width]="135"
                ></fl-picture>
              </fl-link>

              <fl-link
                class="FooterMobileLink mb-mid"
                [link]="'/download'"
                [flTrackingLabel]="'goToIOsApp'"
              >
                <fl-picture
                  alt="Google Play logo"
                  i18n-alt="Footer App Install logo alt text"
                  [display]="PictureDisplay.BLOCK"
                  [height]="40"
                  [loadMethod]="PictureLoadMethod.LAZY"
                  [src]="'footer/google-play.svg'"
                  [width]="135"
                >
                </fl-picture>
              </fl-link>
            </div>

            <fl-social-buttons
              class="FooterSocialIcons"
              [color]="SocialButtonColor.LIGHT"
              [fluid]="true"
            ></fl-social-buttons>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
    <div class="FooterSection">
      <fl-container
        [size]="size"
        [mobilePadding]="true"
      >
        <fl-grid class="FooterBordered">
          <fl-col
            class="tablet:mb-small desktop:mb-none"
            [col]="3"
            [colTablet]="6"
            [colDesktopSmall]="3"
          >
            <p class="FooterStats text-light font-bold mr-xxsmall">
              {{ userCount$ | flAsync | number }}
            </p>
            <p
              class="FooterStatsCopy text-light text-body-16"
              i18n="Footer stats label"
            >
              Registered Users
            </p>
          </fl-col>
          <fl-col
            class="tablet:mb-small desktop:mb-none"
            [col]="3"
            [colTablet]="6"
            [colDesktopSmall]="3"
          >
            <p class="FooterStats text-light font-bold mr-xxsmall">
              {{ projectCount$ | flAsync | number }}
            </p>
            <p
              class="FooterStatsCopy text-light text-body-16"
              i18n="Footer stats label"
            >
              Total Jobs Posted
            </p>
          </fl-col>
          <fl-col [colTablet]="6">
            <!-- don't translate legal text -->
            <p class="text-light text-body-12">
              {{
                'Freelancer &reg; is a registered Trademark of Freelancer Technology Pty Limited (ACN 142 189 759)'
              }}
              {{
                showIndianEntityInfo
                  ? ' &#38; Freelancer Online India Private Limited (CIN U93000HR2011FTC043854)'
                  : ''
              }}
            </p>
            <p class="text-light text-body-12">
              {{ 'Copyright &copy;' }} {{ currentTime | date: 'yyyy' }}
              {{ 'Freelancer Technology Pty Limited (ACN 142 189 759)' }}
            </p>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
  `,
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterFreelancerComponent implements OnInit {
  private datastore = inject(Datastore);
  private timeUtils = inject(TimeUtils);
  private localization = inject(Localization);
  private accessibility = inject(Accessibility);
  private platformId = inject(PLATFORM_ID);

  LogoSize = LogoSize;
  BackgroundColor = BackgroundColor;
  LinkColor = LinkColor;
  IconColor = IconColor;
  PictureDisplay = PictureDisplay;
  PictureLoadMethod = PictureLoadMethod;
  SocialButtonColor = SocialButtonColor;
  Feature = Feature;
  IconSize = IconSize;

  readonly currentTime: number = Date.now();
  userCount$: Observable<number>;
  projectCount$: Observable<number>;
  size: ContainerSize;
  showIndianEntityInfo: boolean;

  @HostBinding('attr.role')
  @Input()
  role = 'contentinfo';
  @Input() hideFooter: boolean;

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  ngOnInit(): void {
    // Don't render the footer stats on the server
    const siteStats$ = isPlatformBrowser(this.platformId)
      ? this.datastore
          .document<SiteStatsCollection>('siteStats')
          .valueChanges()
          .pipe(shareReplay({ bufferSize: 1, refCount: true }))
      : NEVER;

    // Incrementers take the number of user/project created within a day, map
    // it to an interval operator that adds x (mapTo value) to the number, but
    // start off with adding 0 so we get the counter to emit the latest value
    // first.
    const userIncrementer$ = siteStats$.pipe(
      map(counter => counter.userCountPerDay),
      map(countPerDay => 86_400_000 / countPerDay),
      switchMap(incrementSpeed => this.timeUtils.rxInterval(incrementSpeed)),
      map(() => 1),
      startWith(0),
    );

    const projectIncrementer$ = siteStats$.pipe(
      map(counter => counter.projectCountPerDay),
      map(countPerDay => 86_400_000 / countPerDay),
      switchMap(incrementSpeed => this.timeUtils.rxInterval(incrementSpeed)),
      map(() => 1),
      startWith(0),
    );

    this.userCount$ = combineLatest([
      siteStats$.pipe(map(({ userCount }) => userCount)),
      userIncrementer$,
    ]).pipe(map(([userCount, increment]) => userCount + increment));

    this.projectCount$ = combineLatest([
      siteStats$.pipe(map(({ projectCount }) => projectCount)),
      projectIncrementer$,
    ]).pipe(map(([projectCount, increment]) => projectCount + increment));

    this.showIndianEntityInfo =
      this.localization.countryCode?.toUpperCase() === 'IN';
  }

  handleAccessibility(): void {
    this.accessibility.openUserWayWidget();
  }
}
